import { asText } from '@prismicio/helpers';
import classNames from 'classnames';
import React from 'react';
import styled, { css } from 'styled-components';

import {
  PageBodyBlock_Icon_ListFields,
  PageBodyBlock_Icon_ListPrimary,
} from '../../../types/prismic';
import useFadeUp from '../../hooks/useFadeUp';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import { useIsMobile } from '../../utils/breakpoints';
import { Button } from '../ui';

const Section = styled.section<{ siteTag: string }>`
  background: var(--primary);
  .icon {
    height: 24px;
    @media ${media.mdUp} {
      height: auto;
      width: auto;
      max-height: 34px;
      margin-inline-end: 16px;
      transform: translateY(6px);
    }
  }

  .white-icons {
    filter: grayscale() brightness(400%);
  }

  ${({ siteTag }) =>
    siteTag === 'site:profood-solutions' &&
    css`
      background: var(--black);

      h3 {
        color: var(--primary);
        font-weight: 600;
      }
    `}
`;

interface BlockIconListSliceProps {
  slice: {
    primary: PageBodyBlock_Icon_ListPrimary;
    items: PageBodyBlock_Icon_ListFields[];
  };
  sliceIndex: number;
}

const BlockIconListSlice: React.FunctionComponent<BlockIconListSliceProps> = ({
  slice: {
    primary: {
      eyebrow,
      title_start,
      title_middle,
      title_end,
      button,
      slice_anchor,
    },
    items,
  },
  sliceIndex,
}) => {
  const sectionId = slice_anchor?.length
    ? slice_anchor
    : `block-icon-list-${sliceIndex}`;

  useFadeUp(`#${sectionId} .fade-up`);
  const { siteTag } = usePageContext();
  const isMobile = useIsMobile();
  return (
    <Section id={sectionId} siteTag={siteTag}>
      <div className="lg:py-30 container py-10 md:py-24">
        <div className="grid grid-cols-4 gap-x-5 md:grid-cols-8 lg:grid-cols-12">
          <div className="col-span-full mb-10 md:mb-12 lg:col-span-8 lg:col-start-2">
            <div className="eyebrow p5 line color--grey-1 fade-up mb-10 hidden md:block">
              {asText(eyebrow.richText)}
            </div>
            <h2 className="h2 color--grey-1 fade-up">
              {asText(title_start.richText)}{' '}
              <span className="color--primary-light">
                {asText(title_middle.richText)}
              </span>{' '}
              {asText(title_end.richText)}
            </h2>
          </div>
          {items.map((item, idx) => {
            const { heading, description, icon } = item;
            return (
              <div
                key={idx}
                className="fade-up color--grey-1 col-span-4 mb-8 md:mb-12 lg:col-span-6">
                <div className="md:grid md:grid-cols-4 lg:grid-cols-6">
                  <div className="md:col-span-1">
                    <img
                      src={icon?.url}
                      className={classNames(
                        'icon color--primary-light mb-6 md:mb-0',
                        {
                          // Hack for intl migration to force the pngs white
                          'white-icons':
                            siteTag !== 'site:cloudkitchens' &&
                            siteTag !== 'site:profood-solutions' &&
                            icon?.url?.includes('.png'),
                        }
                      )}
                      alt={icon?.alt ?? ''}
                    />
                  </div>
                  <div className="md:col-span-3 lg:col-span-4">
                    <h3 className="h4 mb-2 md:pr-0">
                      {asText(heading.richText)}
                    </h3>
                    <p className="sm-down:pe-1 md:block">
                      {asText(description.richText)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="col-span-full lg:col-start-2">
            <Button
              id={`cta-block-icon-list-slice-${sliceIndex}`}
              globalTypeform
              className=""
              fullWidth={isMobile}
              variant="inverse">
              {asText(button.richText)}
            </Button>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BlockIconListSlice;
